import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Subscription, fromEvent as observableFromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import {
  ResourceId,
  ResourceNote,
  ResourceStatus,
  SaveableReadingIntention,
  SaveableStudyNote,
  StudyNoteMode,
} from '®shared/annotations';
import { User } from '®shared/auth';
import { OnlineResource, getOriginalUrl } from '®shared/bibliographic-resource';
import { FocusService, getItemElementId } from '®shared/focus';
import { InlineNoteEditorType } from '®shared/inline-note';
import { LoadablePageResource } from '®shared/list-item';
import {
  ExternalLinkClickEvent,
  ListItemClickAction,
  OnlineResourceClickEvent,
} from '®shared/list-item/events';
import { ReportBrokenLinkShowModalAction } from '®shared/list-item/report-broken-link-modal/report-broken-link.actions';
import { ShowShareSheetModalAction } from '®shared/list-item/share-sheet';
import { UserPermissions } from '®shared/permissions';
import { PlayerResourceUtils } from '®shared/player-resource';
import { Citation, EditableItem } from '®shared/reading-list/models';
import { LoadableSaveableBoolean } from '®shared/user';

import {
  ListAndItem,
  ResourceIdAndUser,
  ResourceNoteAndUser,
  ResourceStatusAndUser,
  ToggleExpanded,
  WithUser,
} from '../common/events';
import {
  BOOKJACKET_IMAGE_WIDTH_LARGE,
  BOOKJACKET_IMAGE_WIDTH_SMALL,
  ResourceImageUtils,
} from '../common/resource-image-utils';

type SaveableReadingIntentionAliased = SaveableReadingIntention;
type SaveableStudyNoteAliased = SaveableStudyNote;

const ANIMATION_TIME_MS = 10;

function isNoteOnlyItem(item: EditableItem): boolean {
  return item && item.student_note && !item.resource;
}

@Component({
  selector: 'rl-view-item',
  templateUrl: './view-item.component.html',
})
export class ViewItemComponent implements OnChanges, OnDestroy {
  @Input() areAnnotationsResolved: boolean;
  @Input() currentCitation?: Citation;
  @Input() currentUserPermissions: UserPermissions;
  @Input() depth = 0;
  @Input() isExpanded: boolean;
  @Input() isSubscribedToResource: LoadableSaveableBoolean;
  @Input() itemId: string;
  @Input() item?: EditableItem;
  @Input() listId: string;
  @Input() onlineResource?: OnlineResource;
  @Input() pageContent?: LoadablePageResource;
  @Input() readingIntention?: SaveableReadingIntentionAliased;
  @Input() showCitation: boolean;
  @Input() studyNote?: SaveableStudyNoteAliased;
  @Input() user: User;

  /**
   * Many of these outputs emit events of the type ...AndUser.  This is because
   * the parent component does not have direct access to the current User; instead,
   * it has a `user$` observable. To save the parent component needing to unbox
   * `user$` on every event, or to track its own internal user state, we add the
   * current user to outgoing event submissions in this class.
   */
  @Output() changeDimension = new EventEmitter();
  @Output() checkResourceSubscription = new EventEmitter<ResourceIdAndUser>();
  @Output() forceLogin = new EventEmitter<WithUser>();
  @Output() listItemView = new EventEmitter<ListAndItem>();
  @Output() readingIntentionDelete = new EventEmitter<ResourceIdAndUser>();
  @Output() readingIntentionUpdate = new EventEmitter<ResourceStatusAndUser>();
  @Output() showReportBrokenLinkModal =
    new EventEmitter<ReportBrokenLinkShowModalAction>();
  @Output() showShareSheet = new EventEmitter<ShowShareSheetModalAction>();
  @Output() studyNoteDelete = new EventEmitter<ResourceIdAndUser>();
  @Output() studyNoteUpdate = new EventEmitter<ResourceNoteAndUser>();
  @Output() subscribeToResource = new EventEmitter<ResourceIdAndUser>();
  @Output() toggleExpanded = new EventEmitter<ToggleExpanded>();
  @Output() onlineResourceClick = new EventEmitter<OnlineResourceClickEvent>();
  @Output() listItemClick = new EventEmitter<ListItemClickAction>();

  studyNoteMode?: StudyNoteMode;

  resourceId?: string;
  isPlayerResource: boolean;

  // item bookjacket or placeholder image
  resourceImage?: string;
  transitionSubscription: Subscription;

  InlineNoteEditorType = InlineNoteEditorType;
  StudyNoteMode = StudyNoteMode;

  get itemElementId(): string {
    return getItemElementId(this.item.id);
  }

  get itemAriaLabel(): string | null {
    if (this.item.resource) {
      if (this.item.resource.part_of) {
        return (
          this.item.resource.title + ' - ' + this.item.resource.part_of.title
        );
      } else {
        return this.item.resource.title;
      }
    }
    return null;
  }

  get itemHeadingLevel(): number {
    const minHeadingLevel = 2; // Start at 2 since there should only one <h1> on the page (the title)
    return this.depth + minHeadingLevel;
  }

  constructor(
    private elementRef: ElementRef,
    private focusService: FocusService,
  ) {
    // "transitionend" refers to when the list item has finished expanding -
    // the CSS animation is complete.
    // It is de-bounced because two events are fired,
    // one for width and one for max-width properties changing.
    this.transitionSubscription = observableFromEvent(
      this.elementRef.nativeElement,
      'transitionend',
    )
      .pipe(debounceTime(ANIMATION_TIME_MS))
      .subscribe(() => {
        this.onDimensionChange();
      });
  }

  ngOnDestroy() {
    this.transitionSubscription.unsubscribe();
  }

  ngOnChanges(changes?: SimpleChanges) {
    const resource = this.item && this.item.resource;
    this.resourceId = resource && resource.id;

    // add a placeholder image for resource
    if (!this.resourceImage && this.resourceId) {
      this.setResourceImage(BOOKJACKET_IMAGE_WIDTH_SMALL);
    }

    const onlineResourceUrl =
      this.onlineResource && getOriginalUrl(this.onlineResource);
    this.isPlayerResource = PlayerResourceUtils.isPlayerUrl(onlineResourceUrl);
    if (this.isPlayerResource) {
      this.resourceImage =
        PlayerResourceUtils.getThumbnailUrl(onlineResourceUrl);
    }

    // notes have no image, so should resize when initialised
    if (changes && changes.item && isNoteOnlyItem(this.item)) {
      this.onDimensionChange();
    }
  }

  /**
   * Set the resource image by width.
   * @param {number} width - the width in pixels
   */
  setResourceImage(width: number) {
    this.resourceImage = ResourceImageUtils.buildPlaceholderImageURL(
      this.item.resource,
      width,
    );
  }

  /**
   * Triggers the creation/update of a study note, by pushing a mode change
   * into the study-note child component.
   **/
  onStudyNoteEdit() {
    if (this.user.isAnonymous) {
      return this.forceLogin.emit({ user: this.user });
    }

    if (this.studyNote) {
      this.studyNoteMode = StudyNoteMode.EDIT;
    } else {
      this.studyNoteMode = StudyNoteMode.CREATE;
    }
  }

  /**
   * Triggers the creation of a study note, by pushing a mode change
   * into the study-note child component.
   **/
  onStudyNoteReset() {
    this.studyNoteMode = undefined;
    this.onDimensionChange();
  }

  /**
   * Calls the parent event handler for study-note update, but also resets
   * the local state about the study-note editor mode.
   *
   * @param {ResourceNote} $event - the study note event
   **/
  onStudyNoteUpdate($event: ResourceNote) {
    this.focusService.focusOnItemMenu(this.itemId);
    this.studyNoteUpdate.emit({ ...$event, user: this.user });
    this.onStudyNoteReset();
  }

  /**
   * Calls parent event handler with ID of the item the note is associated with
   * @param {ResourceId} $event - the study note event
   **/
  onStudyNoteDelete($event: ResourceId) {
    this.focusService.focusOnItemMenu(this.itemId);
    this.studyNoteDelete.emit({ ...$event, user: this.user });
    this.onDimensionChange();
  }

  /**
   * Adds user to the event, then emits to parent component
   *
   * @param {ResourceStatus} $event - the reading intention event
   **/
  onReadingIntentionUpdate($event: ResourceStatus) {
    this.readingIntentionUpdate.emit({ ...$event, user: this.user });
  }

  /**
   * Adds user to the event, then emits to parent component
   * @param {ResourceId} $event - the reading intention event
   **/
  onReadingIntentionDelete($event: ResourceId) {
    this.readingIntentionDelete.emit({ ...$event, user: this.user });
  }

  /**
   * Updates the list item store by expanding (or collapsing) this item
   **/
  onToggle() {
    const notInitiallyExpanded = !this.isExpanded;

    this.toggleExpanded.emit({
      isExpanded: notInitiallyExpanded,
      itemId: this.itemId,
    });

    this.onListItemDashboardInteraction(notInitiallyExpanded);
    // upscale the bookjacket image for non-Player resources
    if (this.resourceImage && !this.isPlayerResource) {
      this.setResourceImage(BOOKJACKET_IMAGE_WIDTH_LARGE);
    }
  }

  /**
   * Submit an item view interaction to the dashboard for this item.
   *
   * NOTE: no events are fired when item is already expanded
   *
   * @param {boolean} isExpanded - `true` if the item is currently expanded; `false` otherwise
   */
  onListItemDashboardInteraction(isExpanded: boolean) {
    if (isExpanded) {
      this.listItemView.emit({
        listId: this.listId,
        itemId: this.itemId,
      });
    }
  }

  /**
   * Adds a resource to the list of user-subscribed resources
   *
   * @param {ResourceId} resourceId - the ResourceID identifying the resource to which user is subscribing
   */
  onAddToMyBookmarks(resourceId: ResourceId) {
    this.subscribeToResource.emit({ ...resourceId, user: this.user });
    this.onListItemDashboardInteraction(!this.isExpanded);
  }

  /**
   * When the citation is exported for this item, fire tracking events.
   */
  onCitationExport() {
    this.onListItemDashboardInteraction(!this.isExpanded);
    this.listItemClick.emit(ListItemClickAction.EXPORT_CITATION);
  }

  /**
   * Dispatches an action to show the share sheet modal component
   */
  onShareItem(): void {
    this.showShareSheet.emit(
      new ShowShareSheetModalAction({
        item: this.item,
        resourceImage: this.resourceImage,
      }),
    );
  }

  /**
   * When the action menu opens, check resource subscription status
   *
   * @param {boolean} isMenuOpen - true when the menu is open; false otherwise
   */
  onCheckResourceSubscription(isMenuOpen: boolean) {
    const hasSubscriptionOrIsLoading =
      this.isSubscribedToResource &&
      (this.isSubscribedToResource.data ||
        this.isSubscribedToResource.isLoading);

    if (isMenuOpen && !hasSubscriptionOrIsLoading) {
      this.checkResourceSubscription.emit({
        resource: this.resourceId,
        user: this.user,
      });
    }
  }

  onImageLoaded() {
    this.onDimensionChange();
  }

  onDimensionChange() {
    setTimeout(() => {
      this.changeDimension.emit({});
    });
  }

  onOnlineResourceClick($event: ExternalLinkClickEvent) {
    this.onlineResourceClick.emit({
      ...$event,
      item_id: this.itemId,
      list_id: this.listId,
      resource_id: this.resourceId,
    });
  }

  onShowReportBrokenLinkModal(): void {
    if (this.user.isAnonymous) {
      this.forceLogin.emit({ user: this.user });
      return;
    }

    this.showReportBrokenLinkModal.emit(
      new ReportBrokenLinkShowModalAction({
        item: this.item,
        user: this.user,
      }),
    );
  }

  get showAvailabilityButton() {
    return !this.onlineResource || this.onlineResource.type === 'google_books';
  }

  get showViewOnlineButton() {
    return this.onlineResource && this.onlineResource.type !== 'google_books';
  }
}
